import type { ResponseMetadata } from '@delivery-metrics/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type BootstrapSnapshot,
  DashboardV2ApiService,
} from '../services/dashboard-v2-api';

export const DASHBOARD_BOOTSTRAP_QUERY_KEY = 'dashboard-v2-bootstrap';

/**
 * Real freshness for the dashboard header (US-23).
 *
 * **Deliberately not polled.** The figures change once per collector cycle, so
 * a timer would spend the whole day asking a question whose answer it already
 * has. It refetches on window focus, on reconnect, and when the user asks —
 * and because the previous `ETag` goes back with each request, a refetch
 * between rebuilds is a `304` with no body.
 */
export function useDashboardFreshness() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [DASHBOARD_BOOTSTRAP_QUERY_KEY],
    queryFn: () =>
      DashboardV2ApiService.getBootstrap(
        queryClient.getQueryData<BootstrapSnapshot>([
          DASHBOARD_BOOTSTRAP_QUERY_KEY,
        ])
      ),
    // Always revalidate on focus: the point of the indicator is to be right
    // about how old the data is, and revalidation is a 304 when it has not
    // moved.
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const metadata: ResponseMetadata | null = query.isError
    ? null
    : (query.data?.response.metadata ?? null);

  return {
    metadata,
    filterOptions: query.data?.response.filterOptions ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
